import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import {
  ConnectorModel,
  ELMA365ConnectorSettingsOutput,
  MCPServerAuthType,
  MCPServerToolsetSettingsOutput,
} from '../../../../../../gamma-api';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import IbDivider from '../../../../../components/common/IbDivider';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTooltip from '../../../../../components/common/IbTooltip';
import { defaultApi } from '../../../../../apis';

import { FOOTER_CLASS_NAME, SECTION_CLASS_NAME, SKELETON_HEIGHT, SKELETON_WIDTH } from './const';
import BaseToolsetListItem from './BaseToolsetListItem';

import { IToolsetListItemProps } from './index';

const MCPServerToolset: React.FC<IToolsetListItemProps> = ({ toolset, onSetup, onDelete, onToolsetStateChange }) => {
  const [loading, setLoading] = useState(false);
  const [connector, setConnector] = useState<ConnectorModel>();

  const settings = toolset.settings as MCPServerToolsetSettingsOutput;
  const translate = getTranslateFunc('Agent: Toolset: List: Item: MCP Server:');

  const fetchConnector = async () => {
    const authSettings = settings.server_auth.auth_settings;
    if (!authSettings?.connector_id) return;
    try {
      setLoading(true);
      const response = await defaultApi.getOneConnector(authSettings.connector_id);
      setConnector(response.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => void fetchConnector(), [settings.server_auth.auth_settings?.connector_id]);

  const getConnectorInfo = (): string => {
    if (!connector) return '–';
    const elma365ConnectorSettings = connector.settings as ELMA365ConnectorSettingsOutput;
    const authType = translate(`Connector: ${elma365ConnectorSettings.apis.web.auth.type}`);
    return `${connector.name} – ${authType} – ${elma365ConnectorSettings.apis.web.url}`;
  };

  const renderFooter = () => {
    return (
      <>
        <IbDivider />
        <div className={FOOTER_CLASS_NAME}>
          <div className={SECTION_CLASS_NAME}>
            <IbTypography.Paragraph disabled>{translate('Server URL')}</IbTypography.Paragraph>
            <IbTooltip title={settings.server_url}>
              <IbTypography.Paragraph ellipsis>{settings.server_url}</IbTypography.Paragraph>
            </IbTooltip>
          </div>
          <IbDivider type="vertical" />
          {settings.server_auth.auth_type === MCPServerAuthType.Elma365 ? (
            <div className={SECTION_CLASS_NAME}>
              <IbTypography.Paragraph disabled>{translate('Connector')}</IbTypography.Paragraph>
              {loading ? (
                <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />
              ) : (
                <IbTypography.Paragraph>{getConnectorInfo()}</IbTypography.Paragraph>
              )}
            </div>
          ) : (
            <div className={SECTION_CLASS_NAME}>
              <IbTypography.Paragraph disabled>{translate('Auth type')}</IbTypography.Paragraph>
              <IbTypography.Paragraph>
                {translate(`Auth type: ${settings.server_auth.auth_type}`)}
              </IbTypography.Paragraph>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <BaseToolsetListItem
      footer={renderFooter()}
      name={settings.name}
      toolset={toolset}
      onDelete={onDelete}
      onSetup={onSetup}
      onToolsetStateChange={onToolsetStateChange}
    />
  );
};

export default MCPServerToolset;
